import { z } from 'zod';
import { ONBOARDING_PACKET_STATUSES } from 'shared/modules/onboarding-packets/data/OnboardingPacketDTO';

export const PACKET_ANSWER_TYPES = [
  'short_text',
  'long_text',
  'number',
  'boolean',
  'single_select',
  'multi_select',
  'date',
  'document_upload',
] as const;

export const onboardingPacketSchema = z.object({
  guid: z.string(),
  shipper_guid: z.string(),
  template_guid: z.string(),
  status: z.enum(ONBOARDING_PACKET_STATUSES).default('pending'),
  created_at: z.string(),
  updated_at: z.string(),
});

export type OnboardingPacketDTO = z.infer<typeof onboardingPacketSchema>;

export const onboardingPacketShipperSchema = z.object({
  guid: z.string(),
  name: z.string(),
  address: z.string().nullable().default(null),
  city: z.string().nullable().default(null),
  state: z.string().nullable().default(null),
  zip: z.string().nullable().default(null),
  contact_phone: z.string().nullable().default(null),
  contact_email: z.string().nullable().default(null),
});

export type OnboardingPacketShipperDTO = z.infer<
  typeof onboardingPacketShipperSchema
>;

export interface OnboardingPacketWithShipperDTO extends OnboardingPacketDTO {
  shipper: OnboardingPacketShipperDTO | undefined;
}

export const onboardingPacketsPageParamsSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1),
});

export const packetAnswersSchema = z.record(z.string(), z.unknown());

export type PacketAnswersDTO = z.infer<typeof packetAnswersSchema>;

export const PACKET_SUBMISSION_STATUSES = ['pending', 'submitted'] as const;

export type PacketSubmissionStatus =
  (typeof PACKET_SUBMISSION_STATUSES)[number];

export interface PacketSubmissionPayloadDTO {
  answers: PacketAnswersDTO;
  status: PacketSubmissionStatus;
}

export const packetSubmissionSchema = z.object({
  guid: z.string(),
  status: z.enum(PACKET_SUBMISSION_STATUSES),
  submitted_at: z.string().nullable().default(null),
});

export type PacketSubmissionDTO = z.infer<typeof packetSubmissionSchema>;

export const packetFieldSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().default(''),
  answer_type: z.enum(PACKET_ANSWER_TYPES),
  required: z.boolean(),
  options: z.array(z.string()).default([]),
});

export type PacketFieldDTO = z.infer<typeof packetFieldSchema>;

export const onboardingPacketSectionSchema = z.object({
  id: z.string(),
  label: z.string(),
  fields: z.array(packetFieldSchema).default([]),
});

export const onboardingPacketTemplateSchema = z.object({
  name: z.string(),
  instructions: z.string().default(''),
  instructions_enabled: z.boolean().default(false),
  schema: z.object({
    sections: z.array(onboardingPacketSectionSchema).default([]),
  }),
});

export const onboardingPacketDetailsSchema = onboardingPacketSchema.extend({
  template: onboardingPacketTemplateSchema,
  answers: packetAnswersSchema,
});

export type OnboardingPacketDetailsDTO = z.infer<
  typeof onboardingPacketDetailsSchema
>;

export function getPacketFields({
  template,
}: OnboardingPacketDetailsDTO): PacketFieldDTO[] {
  return template.schema.sections.flatMap((section) => section.fields);
}

export function isEmptyPacketAnswer(value: unknown): boolean {
  if (typeof value === 'string') {
    return !value.trim();
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return value === null || value === undefined;
}

function getRequiredPacketFieldMessage({
  label,
  answer_type,
}: PacketFieldDTO): string {
  switch (answer_type) {
    case 'boolean':
    case 'single_select':
      return 'Select Option';
    case 'multi_select':
      return `Select ${label}`;
    case 'document_upload':
      return `Upload ${label}`;
    default:
      return `Enter ${label}`;
  }
}

export function getPacketFieldError(
  field: PacketFieldDTO,
  answers: PacketAnswersDTO,
): string | undefined {
  return field.required && isEmptyPacketAnswer(answers[field.id])
    ? getRequiredPacketFieldMessage(field)
    : undefined;
}

export function createPacketAnswersSchema(
  fields: PacketFieldDTO[],
  shouldValidateRequired: () => boolean,
) {
  return packetAnswersSchema.superRefine((answers, ctx) => {
    if (!shouldValidateRequired()) {
      return;
    }

    for (const field of fields) {
      const message = getPacketFieldError(field, answers);

      if (message) {
        ctx.addIssue({ code: 'custom', path: [field.id], message });
      }
    }
  });
}
